<template>
  <div id="input-orders">
    <v-card id="input-orders-card" class="px-5 py-2 mt-4 mb-5 elevation-0">
      <div :class="{ 'sticky-filters': user.preferences.stickyFilters }">
        <CommonFilters ref="commonFilters" type="order" @filterElements="filterElements">
          <template v-slot:add-button>
            <AddButtons type="order" @clicked="createOrder" />
            <template v-if="!isMobile">
              <ImportButton :click="openImportModal" />
            </template>
            <MoreButtons :buttons="moreButtons" :loading="exportLoading" />
          </template>
        </CommonFilters>
      </div>
      <v-data-table
        id="input-orders-table"
        :headers="headers"
        :items="items"
        :options.sync="options"
        :server-items-length="totalItems"
        sort-by="date"
        :sort-desc="true"
        class="elevation-0 mt-2"
        mobile-breakpoint="0"
        :loading="loading"
        :no-data-text="$t('views.orders.noOrders')"
        :item-class="itemRowBackground"
        :footer-props="{
          'items-per-page-options': defaultPaginationItemsPerPageOptions,
        }"
        :items-per-page="user.preferences.denseTables ? 15 : 10"
        :dense="user.preferences.denseTables"
        @update:options="updateOptions"
      >
        <template v-slot:item.type="{ item }">
          <OrderTableTypeIcon :item="item" />
        </template>
        <template v-slot:item.date="{ item }">
          {{ getDateUserFormat(item.date) }}
        </template>
        <template v-slot:item.broker.alias="{ item }">
          <template
            v-if="
              [
                'buy',
                'sell',
                'switch',
                'switchBuy',
                'spinOff',
                'spinOffBuy',
                'brokerTransfer',
                'sharePremium',
                'scrip',
                'capitalIncrease',
                'bonus',
                'stakeReward',
                'drip',
                'optionBuy',
                'optionSell',
                'commission',
                'paidInterest',
                'interest',
                'interestRetention',
                'deposit',
                'withdrawal',
              ].indexOf(item.type) > -1
            "
          >
            <TableBroker
              :broker="item.broker"
              :newBrokerAlias="
                ['brokerTransfer'].indexOf(item.type) > -1
                  ? item.metadata.newBrokerAlias
                  : undefined
              "
            />
          </template>
          <span v-else></span>
        </template>
        <template v-slot:item.position.ticker="{ item }">
          <TableTicker :position="item.position" />
        </template>
        <template v-slot:item.positionNumber="{ item }">
          <div class="items-flex justify-end">
            <currency
              v-if="
                item.positionNumber &&
                [
                  'buy',
                  'sell',
                  'switch',
                  'switchBuy',
                  'spinOff',
                  'spinOffBuy',
                  'sharePremium',
                  'scrip',
                  'capitalIncrease',
                  'bonus',
                  'stakeReward',
                  'drip',
                  'optionBuy',
                  'optionSell',
                  'commission',
                  'paidInterest',
                  'interest',
                  'interestRetention',
                  'deposit',
                  'withdrawal',
                  'brokerTransfer',
                ].indexOf(item.type) > -1
              "
              :value="item.positionNumber"
              :currency="item.position.type === 'cash' ? item.position.ticker : undefined"
              :decimals="7"
              strong
            />
            <span v-else></span>
          </div>
        </template>
        <template v-slot:item.price="{ item }">
          <div class="items-flex justify-end">
            <currency
              v-if="
                [
                  'buy',
                  'sell',
                  'switch',
                  'switchBuy',
                  'sharePremium',
                  'scrip',
                  'capitalIncrease',
                  'bonus',
                  'stakeReward',
                  'drip',
                  'optionBuy',
                  'optionSell',
                  'commission',
                  'paidInterest',
                  'interest',
                  'interestRetention',
                  'deposit',
                  'withdrawal',
                ].indexOf(item.type) > -1
              "
              :value="item.price"
              :currency="item.position.currency"
              :decimals="3"
            />
            <span v-else></span>
          </div>
        </template>
        <template v-slot:item.total="{ item }">
          <currency v-if="item.total" :value="item.total" :currency="item.position.currency" />
          <span v-else></span>
        </template>
        <template v-slot:item.totalBaseCurrency="{ item }">
          <currency
            v-if="item.totalBaseCurrency"
            :value="item.totalBaseCurrency"
            :currency="user.baseCurrency"
          />
          <span v-else></span>
        </template>
        <template v-slot:item.comission="{ item }">
          <currency
            v-if="
              [
                'buy',
                'sell',
                'switch',
                'switchBuy',
                'sharePremium',
                'scrip',
                'capitalIncrease',
                'bonus',
                'stakeReward',
                'drip',
                'optionBuy',
                'optionSell',
              ].indexOf(item.type) > -1
            "
            :value="item.comission"
            :text-red="!!item.comission"
            :currency="item.comissionCurrency"
            :decimals="5"
          />
          <span v-else></span>
        </template>
        <template v-slot:item.comissionBaseCurrency="{ item }">
          <currency
            v-if="
              [
                'buy',
                'sell',
                'switch',
                'switchBuy',
                'sharePremium',
                'scrip',
                'capitalIncrease',
                'bonus',
                'stakeReward',
                'drip',
                'optionBuy',
                'optionSell',
              ].indexOf(item.type) > -1 && item.comissionBaseCurrency
            "
            :value="item.comissionBaseCurrency"
            :text-red="!!item.comissionBaseCurrency"
            :currency="user.baseCurrency"
          />
          <span v-else></span>
        </template>
        <template v-slot:item.comissionPercentageOfTotalBaseCurrency="{ item }">
          <template v-if="item.comissionPercentageOfTotalBaseCurrency">
            {{ round(item.comissionPercentageOfTotalBaseCurrency).toLocaleString() }}%
          </template>
          <template v-else></template>
        </template>
        <template v-slot:item.taxes="{ item }">
          <currency
            v-if="['buy', 'sell'].indexOf(item.type) > -1"
            :value="item.taxes"
            :text-red="!!item.taxes"
            :currency="item.taxesCurrency"
          />
          <span v-else></span>
        </template>
        <template v-slot:item.exchangeRate="{ item }">
          <TableExchangeRate
            v-if="
              [
                'buy',
                'sell',
                'switch',
                'switchBuy',
                'sharePremium',
                'scrip',
                'capitalIncrease',
                'bonus',
                'stakeReward',
                'drip',
                'optionBuy',
                'optionSell',
              ].indexOf(item.type) > -1
            "
            :item="item"
            type="order"
            @edit="editOrder(item)"
          />
          <span v-else></span>
        </template>
        <template v-slot:item.totalWithComissionBaseCurrency="{ item }">
          <currency
            v-if="item.totalBaseCurrency"
            :value="item.totalWithComissionBaseCurrency"
            :currency="user.baseCurrency"
            strong
          />
          <span v-else></span>
        </template>
        <template v-slot:item.destinationRetentionBaseCurrency="{ item }">
          <currency
            v-if="item.destinationRetentionBaseCurrency"
            :value="item.destinationRetentionBaseCurrency"
            :currency="user.baseCurrency"
          />
          <span v-else></span>
        </template>
        <template v-slot:item.autoFx="{ item }">
          <div
            v-if="
              [
                'buy',
                'sell',
                'switch',
                'switchBuy',
                'scrip',
                'sharePremium',
                'capitalIncrease',
                'bonus',
                'stakeReward',
                'drip',
                'optionBuy',
                'optionSell',
              ].indexOf(item.type) > -1 && item.position.currency !== user.baseCurrency
            "
          >
            <v-icon v-if="item.autoFx">mdi-toggle-switch</v-icon>
            <v-icon v-else>mdi-toggle-switch-off-outline</v-icon>
          </div>
          <span v-else></span>
        </template>
        <template v-slot:item.deductible="{ item }">
          <div v-if="item.deductible !== undefined">
            <v-icon v-if="item.deductible">mdi-toggle-switch</v-icon>
            <v-icon v-else>mdi-toggle-switch-off-outline</v-icon>
          </div>
          <span v-else></span>
        </template>

        <template v-slot:item.tags="{ item }">
          <TagGroup v-if="item.tags.length" :tags="item.tags" />
          <span v-else></span>
        </template>

        <template v-slot:item.actions="{ item }">
          <template v-if="item.description">
            <v-tooltip top max-width="300px">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  color="secondary"
                  v-bind="attrs"
                  small
                  class="mr-4"
                  style="margin-top: 3px;"
                  v-on="on"
                >
                  mdi-message-bulleted
                </v-icon>
              </template>
              <span>{{ item.description }}</span>
            </v-tooltip>
          </template>
          <template v-if="['switchBuy', 'spinOffBuy'].indexOf(item.type) === -1">
            <template
              v-if="
                ['optionBuy', 'optionSell'].indexOf(item.type) > -1 &&
                item.position.positionNumber !== 0
              "
            >
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    v-bind="attrs"
                    size="18"
                    class="mr-3"
                    v-on="on"
                    @click="closeOptionOrder(item)"
                  >
                    mdi-checkbox-multiple-outline
                  </v-icon>
                </template>
                <span>{{ $t('views.orders.ordersTable.closeOptionOrder.label') }}</span>
              </v-tooltip>
            </template>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  size="16"
                  class="mr-2"
                  v-on="on"
                  @click="duplicateOrder(item)"
                >
                  mdi-content-copy
                </v-icon>
              </template>
              <span>{{ $t('views.orders.ordersTable.duplicateOrder') }}</span>
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" size="18" class="mr-2" v-on="on" @click="editOrder(item)">
                  mdi-pencil-outline
                </v-icon>
              </template>
              <span>{{ $t('common.edit') }}</span>
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon size="18" v-bind="attrs" v-on="on" @click="deleteOrder(item)">
                  mdi-delete-outline
                </v-icon>
              </template>
              <span>{{ $t('common.delete') }}</span>
            </v-tooltip>
          </template>
        </template>
      </v-data-table>

      <OrdersButtonsModal
        :isModalActive.sync="isOrdersButtonsModalActive"
        @selected="createOrder"
      />
      <OrderModal
        :isModalActive.sync="isOrderModalActive"
        :typeOrderModal="typeOrderModal"
        :subTypeOrderModal="subTypeOrderModal"
        :editing="editing"
        :editItem="editItem"
        :duplicating="duplicating"
        @saved="(obj) => loadItems({ ...obj })"
      />
      <ImportModal
        ref="ImportModal"
        :isModalActive.sync="isImportModalActive"
        type="order"
        @init="loadItems"
      />
      <HeadersModal
        v-if="isHeadersModalActive"
        :isModalActive.sync="isHeadersModalActive"
        :tableName="tableName"
        :defaultHeaders="defaultHeaders"
      />
    </v-card>
  </div>
</template>

<script>
import { DateTime } from 'luxon'
import FileSaver from 'file-saver'
import { get } from 'lodash'
import { mapState } from 'vuex'
import { getOrders, deleteOrder, exportOrdersCsv, closeOptionOrder } from '@/lib/api.js'
import { getDateUserFormat } from '@/lib/utils/date.js'
import { getCurrencySymbol } from '@/lib/utils/currencies.js'
import { round } from '@/lib/utils/math.js'
import tableMixin from '@/views/components/core/tableMixin.js'
import CommonFilters from '@/views/components/common/CommonFilters'
import Currency from '@/views/components/common/Currency'
import AddButtons from '@/views/components/common/AddButtons'
import MoreButtons from '@/views/components/common/MoreButtons'
import OrderTableTypeIcon from '@/views/components/common/orders/OrderTableTypeIcon'
import TableTicker from '@/views/components/common/TableTicker'
import TableBroker from '@/views/components/common/TableBroker'
import TableExchangeRate from '@/views/components/common/TableExchangeRate'
import OrdersButtonsModal from '@/views/components/common/orders/OrdersButtonsModal'
import OrderModal from '@/views/components/common/orders/OrderModal'
import ImportButton from '@/views/components/imports/ImportButton'
import ImportModal from '@/views/components/imports/ImportModal'
import TagGroup from '@/views/components/common/TagGroup'
import gtag from '@/lib/gtag'

export default {
  components: {
    CommonFilters,
    OrdersButtonsModal,
    OrderModal,
    Currency,
    AddButtons,
    MoreButtons,
    OrderTableTypeIcon,
    TableTicker,
    TableBroker,
    TableExchangeRate,
    ImportButton,
    ImportModal,
    TagGroup,
  },
  mixins: [tableMixin],
  data: () => ({
    exportLoading: false,
    filters: {},
    isOrdersButtonsModalActive: false,
    isOrderModalActive: false,
    isImportModalActive: false,
    editing: false,
    editItem: {},
    duplicating: false,
    typeOrderModal: null,
    subTypeOrderModal: null,
    tableName: 'orders',
  }),
  computed: {
    ...mapState(['user']),
    isMobile() {
      return this.$vuetify.breakpoint.xs
    },
    defaultHeaders() {
      return [
        {
          text: '',
          value: 'type',
          customSecondaryText: this.$t('views.orders.ordersTable.type'),
          sortable: false,
        },
        { text: this.$t('views.orders.ordersTable.date'), value: 'date' },
        {
          text: this.$t('views.orders.ordersTable.broker'),
          value: 'broker.alias',
          sortable: false,
        },
        {
          text: this.$t('views.orders.ordersTable.ticker'),
          value: 'position.ticker',
          sortable: false,
        },
        {
          text: this.$t('views.orders.ordersTable.positionNumber'),
          value: 'positionNumber',
          align: 'right',
        },
        { text: this.$t('views.orders.ordersTable.price'), value: 'price', align: 'right' },
        { text: this.$t('views.orders.ordersTable.total'), value: 'total', align: 'right' },
        { text: this.$t('views.orders.ordersTable.comission'), value: 'comission', align: 'right' },
        {
          text: `${this.$t('views.orders.ordersTable.comission')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'comissionBaseCurrency',
          align: 'right',
          customHidden: true,
        },
        {
          text: `${this.$t('views.orders.ordersTable.comissionPercentageOfTotalBaseCurrency')}`,
          value: 'comissionPercentageOfTotalBaseCurrency',
          align: 'right',
          customHidden: true,
        },
        {
          text: this.$t('views.orders.ordersTable.taxes'),
          value: 'taxes',
          align: 'right',
          customHidden: true,
        },
        {
          text: this.$t('views.orders.ordersTable.exchangeRate'),
          value: 'exchangeRate',
          align: 'right',
        },
        {
          text: `${this.$t('views.orders.ordersTable.total')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'totalBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t('views.orders.ordersTable.totalWithComission')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'totalWithComissionBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t('views.orders.ordersTable.destinationRetention')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'destinationRetentionBaseCurrency',
          align: 'right',
          customHidden: true,
        },
        {
          text: `${this.$t('views.orders.ordersTable.autoFx')}`,
          value: 'autoFx',
          align: 'right',
          sortable: false,
        },
        {
          text: `${this.$t('views.orders.ordersTable.deductible')}`,
          value: 'deductible',
          align: 'right',
          sortable: false,
          customHidden: true,
        },
        { text: this.$t('views.orders.ordersTable.tags'), value: 'tags', customHidden: true },
        {
          text: '',
          value: 'actions',
          sortable: false,
          align: 'right',
          customHideDisabled: true,
          customSecondaryText: this.$t('views.orders.ordersTable.editRemove'),
        },
      ]
    },
    moreButtons() {
      const moreButtons = []
      if (this.isMobile) {
        moreButtons.push({
          value: 'import',
          title: this.$t('views.common.moreButtons.import'),
          icon: 'mdi-file-upload-outline',
          action: this.openImportModal,
        })
      }
      moreButtons.push({
        value: 'download',
        title: this.$t('views.common.moreButtons.download'),
        icon: 'mdi-cloud-download-outline',
        action: this.downloadCsv,
        disabled: !this.items.length,
      })
      moreButtons.push({
        value: 'customHeaders',
        title: this.$t('views.common.headersModal.title'),
        icon: 'mdi-cog-outline',
        action: this.customHeaders,
      })
      return moreButtons
    },
  },
  watch: {
    '$route.query': {
      handler() {
        if (this.$route.query.open) {
          if (this.$route.query.open === 'order') {
            this.createOrder(this.$route.query.type)
          } else if (this.$route.query.open === 'import') {
            this.openImportModal({ broker: this.$route.query.broker })
          }
        }
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    getDateUserFormat,
    round,
    itemRowBackground(item) {
      if (item.exchangeRate === null) return 'row-advise'
    },
    openOrdersButtonsModal() {
      this.isOrdersButtonsModalActive = true
    },
    openImportModal({ broker } = {}) {
      this.isImportModalActive = true
      if (broker) {
        setTimeout(() => {
          this.$refs.ImportModal.preSelectBroker({ broker })
        }, 500)
      }

      gtag.pageview({
        pagePath: `/input/orders/import`,
        pageTitle: `input|orders|import`,
        pageContentGroup: 'modal',
      })
    },
    createOrder(type, subType) {
      if (type === 'moreOptions') {
        this.openOrdersButtonsModal()
        return
      }

      this.editing = false
      this.duplicating = false
      this.isOrderModalActive = true
      this.typeOrderModal = type
      this.subTypeOrderModal = subType

      gtag.pageview({
        pagePath: `/input/orders/create/${this.typeOrderModal}`,
        pageTitle: `input|orders|create|${this.typeOrderModal}`,
        pageContentGroup: 'modal',
      })
    },
    editOrder(order) {
      this.editing = true
      this.duplicating = false
      this.isOrderModalActive = true
      this.typeOrderModal = order.type
      this.subTypeOrderModal = ['fund', 'plan'].indexOf(order.positionType) > -1 ? 'fund' : 'cash'
      this.editItem = JSON.parse(JSON.stringify(order))

      gtag.pageview({
        pagePath: `/input/orders/edit/${this.typeOrderModal}`,
        pageTitle: `input|orders|edit|${this.typeOrderModal}`,
        pageContentGroup: 'modal',
      })
    },
    duplicateOrder(order) {
      this.editing = false
      this.duplicating = true
      this.isOrderModalActive = true
      this.typeOrderModal = order.type
      this.subTypeOrderModal = ['fund', 'plan'].indexOf(order.positionType) > -1 ? 'fund' : 'cash'
      this.editItem = JSON.parse(JSON.stringify(order))

      gtag.pageview({
        pagePath: `/input/orders/duplicate/${this.typeOrderModal}`,
        pageTitle: `input|orders|duplicate|${this.typeOrderModal}`,
        pageContentGroup: 'modal',
      })
    },
    async closeOptionOrder(order) {
      if (
        await this.$root.$confirm({
          title: this.$t('views.orders.ordersTable.closeOptionOrder.confirm.title'),
          message: this.$t('views.orders.ordersTable.closeOptionOrder.confirm.message'),
          okColor: 'primary',
        })
      ) {
        this.loading = true
        try {
          await closeOptionOrder({ order })
          await this.loadItems()
          this.$root.$snackbar.success({ text: this.$t('common.saveOk') })
        } catch (error) {
          const errorCode = get(error, 'data.name', null)
          this.$root.$snackbar.error({
            text: errorCode ? this.$t(`apiErrors.${errorCode}`) : this.$t('common.deleteError'),
            timeout: 6000,
          })
        }
        this.loading = false
      }
    },
    async loadItems({ newPositionCreated = false } = {}) {
      this.loading = true
      try {
        const filter = JSON.parse(
          JSON.stringify({
            isFxOrder: { $ne: true },
            ...this.filters,
          })
        )
        const { page, limit, sort } = this.getTablePaginationOptions(this.options)
        const response = await getOrders({ filter, page, limit, sort })
        this.setTablePaginatedResponse(response)

        if (newPositionCreated && this.$refs.commonFilters) {
          this.$refs.commonFilters.updateFilters()
        }
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }
      this.loading = false
    },
    async deleteOrder(order) {
      if (
        await this.$root.$confirm({
          title: this.$t('views.orders.ordersTable.confirm.title'),
          message: this.$t('views.orders.ordersTable.confirm.message'),
        })
      ) {
        this.loading = true
        try {
          await deleteOrder({ order })
          await this.loadItems()
          this.$root.$snackbar.success({ text: this.$t('common.deleteOk') })

          gtag.pageview({
            pagePath: `/input/orders/delete/${order.type}/success`,
            pageTitle: `input|orders|delete|${order.type}|success`,
            pageContentGroup: 'modal',
          })
        } catch (error) {
          const errorCode = get(error, 'data.name', null)
          this.$root.$snackbar.error({
            text: errorCode ? this.$t(`apiErrors.${errorCode}`) : this.$t('common.deleteError'),
            timeout: 6000,
          })

          gtag.pageview({
            pagePath: `/input/orders/delete/${order.type}/error`,
            pageTitle: `input|orders|delete|${order.type}|error`,
            pageContentGroup: 'modal',
          })
        }
        this.loading = false
      }
    },
    async downloadCsv() {
      this.exportLoading = true
      const nowFormatted = DateTime.now().toFormat('yyyy_MM_dd HH_mm_ss')
      try {
        const result = await exportOrdersCsv({
          filter: {
            isFxOrder: { $ne: true },
            ...this.filters,
          },
        })
        const blob = new Blob([result.data], { type: 'text/csv;charset=utf-8' })
        FileSaver.saveAs(blob, `Filios - ${this.$t('views.input.tabs.orders')} ${nowFormatted}.csv`)

        gtag.pageview({
          pagePath: `/input/orders/export/success`,
          pageTitle: `input|orders|export|success`,
          pageContentGroup: 'modal',
        })
      } catch (error) {
        const errorCode = get(error, 'data.name', null)
        if (errorCode === 'orders.limitReached') {
          this.$root.$snackbar.error({
            text: this.$t('views.orders.ordersTable.limitReached'),
            timeout: 10000,
          })
        } else {
          this.$root.$snackbar.checkAndOpenCommonLoadError(error)
        }

        gtag.pageview({
          pagePath: `/input/orders/export/error`,
          pageTitle: `input|orders|export|error`,
          pageContentGroup: 'modal',
        })
      }

      this.exportLoading = false
    },
  },
}
</script>

<style lang="scss">
#input-orders {
  #input-orders-card {
    .items-flex {
      display: flex;
      align-items: center;
      &.justify-end {
        justify-content: flex-end;
      }
    }
  }
}
</style>
